"use client";
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useContactModal } from '@/contexts/ContactModalContext';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const MONO = '"SF Mono", "Roboto Mono", Menlo, Consolas, monospace';
const INK = '#080400';
const PURPLE = '#3730a3';
const LIME = '#ceff1a';

const questions = [
    { q: 'Which channels generate qualified leads?', a: 'Google Ads, Organic, Referral' },
    { q: 'Which campaigns are wasting budget?', a: '3 flagged, low ROAS' },
    { q: 'Where are users dropping off?', a: 'Checkout step 2, 41%' },
    { q: 'Which audiences convert best?', a: 'Retargeting, 18–34' },
    { q: 'What is our customer acquisition cost?', a: 'Trending -12% MoM' },
    { q: 'Which campaigns contribute to revenue?', a: 'Top 4 of 22 active' },
    { q: 'Where should the next marketing rupee be invested?', a: 'Model recalculating…' },
];

export default function AnalyticsShouldAnswer() {
    const { openModal } = useContactModal();
    const [draft, setDraft] = useState('');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!draft.trim()) return;
        openModal();
    };

    return (
        <section className="relative overflow-hidden py-20 md:py-28 px-6 md:px-16 lg:px-24" style={{ background: '#ffffff', borderTopLeftRadius: '80px', borderTopRightRadius: '80px', borderBottomLeftRadius: '80px', borderBottomRightRadius: '80px' }}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.035]" style={{ backgroundImage: 'radial-gradient(circle, rgba(55,48,163,1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

            <div className="max-w-[1100px] mx-auto relative z-10 flex flex-col gap-10">
                <div className="flex flex-col gap-6 max-w-[760px]">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.2em] uppercase"
                        style={{ color: PURPLE, fontFamily: CLASH }}
                    >
                        Beyond the Numbers
                    </motion.span>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: CLASH, color: INK }}
                    >
                        What Good Marketing Measurement Should Answer
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                        className="text-base md:text-lg leading-relaxed"
                        style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.6)', letterSpacing: '0.02em' }}
                    >
                        Analytics isn&apos;t valuable because it gives you more numbers. It&apos;s valuable when it helps answer questions like:
                    </motion.p>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
                    className="relative rounded-2xl overflow-hidden"
                    style={{ background: '#0c0a08', border: '1px solid rgba(8,4,0,0.08)', boxShadow: '0 40px 90px -30px rgba(8,4,0,0.35)' }}
                >
                    <div className="flex items-center gap-2 px-5 py-3.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}>
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#ff5f56' }} />
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#ffbd2e' }} />
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#27c93f' }} />
                        <span className="ml-2 text-[11px]" style={{ fontFamily: MONO, color: 'rgba(255,255,255,0.4)' }}>
                            measurement.log
                        </span>
                        <span className="ml-auto flex items-center gap-1.5 text-[10px]" style={{ fontFamily: MONO, color: 'rgba(206,255,26,0.7)' }}>
                            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: LIME }} />
                            live
                        </span>
                    </div>

                    <div className="flex flex-col">
                        {questions.map((item, i) => (
                            <motion.div
                                key={item.q}
                                initial={{ opacity: 0, x: -12 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: 0.3 + i * 0.08 }}
                                className="group relative flex items-start gap-4 px-5 md:px-6 py-4 md:py-5 transition-colors duration-300 hover:bg-white/[0.03]"
                                style={{ borderBottom: i === questions.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.06)' }}
                            >
                                <span
                                    className="shrink-0 text-[11px] md:text-xs pt-0.5 select-none"
                                    style={{ fontFamily: MONO, color: 'rgba(255,255,255,0.25)' }}
                                >
                                    {String(i + 1).padStart(2, '0')}
                                </span>
                                <span className="shrink-0 text-sm pt-0.5" style={{ fontFamily: MONO, color: '#a5b4fc' }}>
                                    ?
                                </span>
                                <div className="flex-1 flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 min-w-0">
                                    <p
                                        className="text-sm md:text-base font-bold leading-snug"
                                        style={{ fontFamily: CLASH, color: '#ffffff' }}
                                    >
                                        {item.q}
                                    </p>
                                    <span
                                        className="text-[11px] md:text-xs shrink-0 sm:ml-auto opacity-70 group-hover:opacity-100 transition-opacity"
                                        style={{ fontFamily: MONO, color: LIME }}
                                    >
                                        → {item.a}
                                    </span>
                                </div>
                            </motion.div>
                        ))}
                        <form onSubmit={handleSubmit} className="flex items-center gap-2 px-5 md:px-6 py-4" style={{ fontFamily: MONO }}>
                            <span className="text-[11px] shrink-0" style={{ color: 'rgba(255,255,255,0.25)' }}>08</span>
                            <span className="text-sm shrink-0" style={{ color: '#a5b4fc' }}>?</span>
                            <input
                                type="text"
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                placeholder="type your question and hit enter…"
                                className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm placeholder:text-white/25"
                                style={{ fontFamily: MONO, color: '#ffffff', caretColor: LIME }}
                            />
                            <button
                                type="submit"
                                aria-label="Ask Lead Workz"
                                className="shrink-0 text-[11px] font-bold uppercase tracking-[0.1em] px-3 py-1.5 rounded-full transition-opacity hover:opacity-90"
                                style={{ background: LIME, color: INK, opacity: draft.trim() ? 1 : 0.35, fontFamily: CLASH }}
                            >
                                Ask →
                            </button>
                        </form>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
